import React from "react";
import { getAppParams } from "../utils/appParams";
import {
  PDFUIInstanceContext,
  PDFUIRenderToElementContext,
} from "./FoxitWebPDFContexts";
import { licenseKey, licenseSN } from "../license-key";
import axios from "axios";
import { Modal, Input, message } from "antd";
import { buildServiceNowUrl, getServiceNowHeaders } from "../services/serviceNow";

if (module.hot) {
  module.hot.dispose(() => {
    window.location.reload();
  });
}

class FoxitWebPDFAppComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      containerRef: React.createRef(),
      pdfui: null,
      rendered: false,
    };
  }

  handleSave = async () => {
    const pfdDoc = await this.state.pdfui.getCurrentPDFDoc();
    const file = await pfdDoc.getFile();

    let _filePath = sessionStorage.getItem("filePath");
    const driveName = _filePath.split("/")[0];
    _filePath = _filePath.replace(driveName + "/", "");

    const blob = new Blob([file], { type: "application/pdf" });

    // Re-write file logic
    this.props.uploadPdfToSharePoint(blob, _filePath);
  };

  handleSaveAsNew = async (
    fileName = sessionStorage.getItem("filePath"),
    pageRange = ""
  ) => {
    if (fileName === "") {
      fileName = sessionStorage.getItem("filePath");
    }
    if (!fileName.includes(".pdf")) {
      fileName = fileName + ".pdf";
    }

    const pfdDoc = await this.state.pdfui.getCurrentPDFDoc();
    const file = await pfdDoc.getFile();
    let blobFile;
    let totalPageRange = [];
    if (pageRange !== "") {
      const ranges = pageRange.split(",").map((r) => r.trim());
      ranges.forEach((range) => {
        if (range.includes("-")) {
          const [start, end] = range
            .split("-")
            .map((num) => parseInt(num.trim(), 10) - 1);
          totalPageRange.push([start, end]);
        } else {
          const singlePage = parseInt(range.trim(), 10) - 1;
          totalPageRange.push([singlePage]);
        }
      });
      const extractedPagesArrayBuffers = await pfdDoc.extractPages(
        totalPageRange
      );
      blobFile = new Blob(extractedPagesArrayBuffers, {
        type: "application/pdf",
      });
    } else {
      blobFile = new Blob([file], { type: "application/pdf" });
    }
    let _filePath = sessionStorage.getItem("filePath");
    const driveName = _filePath.split("/")[0];

    _filePath = _filePath.replace(driveName + "/", "");

    // check if filepath as any directory in it
    const directory = _filePath.split("/").slice(0, -1).join("/");
    if (directory) {
      fileName = directory + "/" + fileName;
    }

    this.props.uploadPdfToSharePoint(blobFile, fileName);
  };

  closeModal = () => {
    this.setState({ saveAsNewModal: false });
  };

  showModal = () => {
    this.setState({ saveAsNewModal: true });
  };

  componentDidMount() {
    console.log("[Foxit] componentDidMount - start", { hasFileBuffer: !!this.props.fileBuffer, hasFileBlob: !!this.props.fileBlob, hasFileUrl: !!this.props.fileUrl });
    this.waitForUIExtension();
  }

  waitForUIExtension = () => {
    const start = Date.now();
    const interval = setInterval(() => {
      if (window.UIExtension && window.UIExtension.PDFUI) {
        clearInterval(interval);
        this.initializeFoxit();
      }
      if (Date.now() - start > 10000) {
        clearInterval(interval);
        console.error("[Foxit] UIExtension failed to load after 10s");
      }
    }, 100);
  }

  initializeFoxit = () => {
    const libPath = (typeof window !== 'undefined' && window.__foxitLibBase) ? window.__foxitLibBase : "/foxit-lib/";
    // Ensure Foxit UIX addons are loaded so all ribbon tabs/features are available
    const ensureAddonsLoaded = async () => {
      if (typeof window === 'undefined') return;
      if (window.UIXAddons && Array.isArray(window.UIXAddons) && window.UIXAddons.length > 0) return;
      try {
        const isMobile = !!(window.UIExtension && window.UIExtension.PDFViewCtrl && window.UIExtension.PDFViewCtrl.DeviceInfo && window.UIExtension.PDFViewCtrl.DeviceInfo.isMobile);
        const addonSrc = libPath + 'uix-addons/' + (isMobile ? 'allInOne.mobile.js' : 'allInOne.js');
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = addonSrc;
          s.async = true;
          s.onload = () => resolve();
          s.onerror = (e) => reject(e);
          document.head.appendChild(s);
        });
      } catch (_) {}
    };

    // Build config; we'll set renderTo to a dedicated child element
    const buildConfig = (renderTarget) => ({
      viewerOptions: {
        libPath,
        jr: {
          workerPath: libPath,
          enginePath: libPath + "jr-engine/gsdk",
          fontPath: "https://webpdf.foxitsoftware.com/webfonts/",
          licenseSN,
          licenseKey,
          enableServiceWorker: false
        }
      },
      renderTo: renderTarget,
      appearance: (window.UIExtension && window.UIExtension.appearances)
        ? window.UIExtension.appearances.ribbon
        : undefined,
      fragments: [],
      addons: (typeof window !== 'undefined' && window.UIXAddons) ? window.UIXAddons : []
    });

    const attachAndCreate = async () => {
      const container = this.state.containerRef.current;
      if (!container) {
        setTimeout(attachAndCreate, 50);
        return;
      }
      // Create a dedicated render element and attach BEFORE creating PDFUI
      const renderToElement = document.createElement('div');
      renderToElement.id = 'pdf-ui';
      renderToElement.style.position = 'absolute';
      renderToElement.style.inset = '0';
      renderToElement.style.width = '100%';
      renderToElement.style.height = '100%';
      try { container.innerHTML = ''; container.appendChild(renderToElement); } catch (_) {}
      try {
        // Load UIX addons (full bundle) before creating PDFUI so all features/tabs appear
        await ensureAddonsLoaded();
        const pdfui = new window.UIExtension.PDFUI(buildConfig(renderToElement));
        this.setState({ pdfui });
        this.renderToElement = renderToElement;
        this.attachPromise = Promise.resolve();

        window.addEventListener(
          "resize",
          (this.resize = () => {
            try { pdfui.redraw(); } catch (e) {}
          })
        );

        // Proceed to open source if present
        if ((this.props.fileBuffer || this.props.fileBlob || this.props.fileUrl) && !this.state.rendered) {
          this.openFromSource(pdfui);
        }
      } catch (error) {
        console.error('[Foxit] Failed to create PDFUI after attach', error);
      }
    };

    try {
      attachAndCreate();

      // listeners and open handled after creation
    } catch (error) {
      console.error('[Foxit] Failed to initialize Foxit', error);
    }
  }

  openFromSource = async (pdfui) => {
    try {
      if (typeof this.props.onOpenStart === 'function') {
        try { this.props.onOpenStart(); } catch (_) {}
      }
      if (this.attachPromise) {
        try { await this.attachPromise; } catch (_) {}
      }
      let buffer;
      if (this.props.fileBuffer && typeof this.props.fileBuffer.arrayBuffer === 'function') {
        buffer = await this.props.fileBuffer.arrayBuffer();
      } else if (this.props.fileBlob && typeof this.props.fileBlob.arrayBuffer === 'function') {
        buffer = await this.props.fileBlob.arrayBuffer();
      } else if (this.props.fileUrl) {
        const resp = await fetch(this.props.fileUrl);
        const blob = await resp.blob();
        buffer = await blob.arrayBuffer();
      }
      if (buffer) {
        this.setState({ ...this.state, rendered: true });
        await pdfui.openPDFByFile(buffer, { name: 'document.pdf' });
        if (typeof this.props.onOpened === 'function') {
          try { this.props.onOpened(); } catch (_) {}
        }
      }
    } catch (e) {
      console.error('[Foxit] openFromSource error', e);
      if (typeof this.props.onOpenError === 'function') {
        try { this.props.onOpenError(e); } catch (_) {}
      }
    }
  }

  render() {
    return (
      <>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px",
            backgroundColor: "#f0f0f0",
            borderBottom: "1px solid #ddd",
          }}
        >
          <h3 style={{ margin: 0 }}>PDF Viewer</h3>
          <div style={{ display: "flex", gap: 6, alignItems: 'center' }}>
            <button className="fv__ui-toolbar-btn" onClick={this.savePdfAction} title="Save current document">
              Save
            </button>
            <button className="fv__ui-toolbar-btn" onClick={this.saveAsPdfAction} title="Save As...">
              Save As
            </button>
            <button className="fv__ui-toolbar-btn" onClick={this.extractPagesAction} title="Extract specific pages into a new PDF">
              <span className="fv__ui-toolbar-icon fv__icon-extract" />
            </button>
            <button className="fv__ui-toolbar-btn" onClick={this.burstPdfAction} title="Split the PDF into multiple files">
              <span className="fv__ui-toolbar-icon fv__icon-burst" />
            </button>
            <button className="fv__ui-toolbar-btn" onClick={() => window.close()} title="Close">
              <span className="fv__ui-toolbar-icon" style={{ backgroundImage: "url('data:image/svg+xml,%3Csvg xmlns=%5C'http://www.w3.org/2000/svg%5C' width=%5C'20%5C' height=%5C'20%5C' viewBox=%5C'0 0 24 24%5C' fill=%5C'none%5C' stroke=%5C'%23333%5C' stroke-width=%5C'2%5C' stroke-linecap=%5C'round%5C' stroke-linejoin=%5C'round%5C'%3E%3Cpath d=%5C'M6 6l12 12M18 6L6 18%5C'/%3E%3C/svg%3E')" }} />
            </button>
          </div>
        </div>

        <PDFUIRenderToElementContext.Provider value={this.state.containerRef}>
          {this.state.pdfui ? (
            <PDFUIInstanceContext.Provider value={this.state.pdfui}>
              {this.props.children}
            </PDFUIInstanceContext.Provider>
          ) : (
            this.props.children
          )}
        </PDFUIRenderToElementContext.Provider>
      </>
    );
  }

  async downloadPdf(fileName) {
    // await this.props.uploadPdfToSharePoint(blob);
  }

  componentDidUpdate() {
    const pdfui = this.state.pdfui;
    const container = this.state.containerRef.current;
    if (pdfui && container && this.renderToElement) {
      if (this.renderToElement.parentElement !== container) {
        try {
          container.innerHTML = '';
          container.appendChild(this.renderToElement);
        } catch (e) {}
      }
    }
    if (pdfui && !this.state.rendered && (this.props.fileBuffer || this.props.fileBlob || this.props.fileUrl)) {
      (async () => {
        try {
          let buffer;
          if (this.props.fileBuffer && typeof this.props.fileBuffer.arrayBuffer === 'function') {
            buffer = await this.props.fileBuffer.arrayBuffer();
          } else if (this.props.fileBlob && typeof this.props.fileBlob.arrayBuffer === 'function') {
            buffer = await this.props.fileBlob.arrayBuffer();
          } else if (this.props.fileUrl) {
            const resp = await fetch(this.props.fileUrl);
            const blob = await resp.blob();
            buffer = await blob.arrayBuffer();
          }
          if (buffer) {
            this.setState({ ...this.state, rendered: true });
            console.log("[Foxit] Opening PDF by file (didUpdate) - bytes:", buffer.byteLength);
            await pdfui.openPDFByFile(buffer);
            console.log("[Foxit] PDF opened (didUpdate)");
          }
        } catch (_) {}
      })();
    }
  }
  componentWillUnmount() {
    if (this.state.pdfui) {
      this.state.pdfui.destroy();

      window.removeEventListener("resize", this.resize);
    }
  }
}
export const FoxitWebPDFApp = React.memo(FoxitWebPDFAppComponent);

// ----- Additional instance methods for Extract/Burst -----
// NOTE: Saves go through the app's same-origin /api path.
FoxitWebPDFAppComponent.prototype.__describeAxiosError = function (e) {
  try {
    const resp = e?.response;
    const apiMsg =
      resp?.data?.error ||
      resp?.data?.message ||
      (typeof resp?.data === 'string' ? resp.data : null);
    if (apiMsg) return String(apiMsg);
    return String(e?.message || e);
  } catch (_) {
    return String(e?.message || e);
  }
};

FoxitWebPDFAppComponent.prototype.__describeFormDataForLog = function (formData) {
  const summary = {};
  try {
    if (!formData || typeof formData.forEach !== 'function') return summary;
    formData.forEach((value, key) => {
      if (value instanceof Blob) {
        summary[key] = {
          type: value.type,
          size: value.size,
        };
      } else {
        summary[key] = value;
      }
    });
  } catch (err) {
    console.warn('[FoxitWebPDFApp] Failed to serialize form data for logging', err);
  }
  return summary;
};

FoxitWebPDFAppComponent.prototype.__logRequestDetails = function (label, details) {
  try {
    console.log(`[FoxitWebPDFApp] ${label}`, details);
  } catch (_) {}
};

FoxitWebPDFAppComponent.prototype.__getSmartFoldersContext = function () {
  try {
    const params = getAppParams();
    const table = params.get('table');
    const sys_id = params.get('sys_id');
    const user_sys_id =
      params.get('user_sys_id') ||
      params.get('userSysId') ||
      params.get('sn_user_sys_id');
    if (!table || !sys_id) return null;
    return { table, sys_id, user_sys_id };
  } catch (err) {
    console.warn('[FoxitWebPDFApp] Failed to parse Smart Folders context', err);
    return null;
  }
};

FoxitWebPDFAppComponent.prototype.__blobToBase64 = async function (blob) {
  if (!blob) return '';
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        const commaIdx = result.indexOf(',');
        resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
      } else {
        reject(new Error('Unexpected FileReader result'));
      }
    };
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(blob);
  });
};

FoxitWebPDFAppComponent.prototype.__uploadViaSmartFolders = async function ({
  blob,
  fileName,
}) {
  const context = this.__getSmartFoldersContext();
  if (!context) return { handled: false, reason: 'missing-context' };
  if (!context.user_sys_id) return { handled: false, reason: 'missing-user-sys-id' };

  // Upload through the same-origin /api proxy path.
  const base64 = await this.__blobToBase64(blob);
  const payload = {
    table_name: context.table,
    user_sys_id: context.user_sys_id,
    record_sys_id: context.sys_id,
    file_name: fileName,
    content_type: blob?.type || 'application/pdf',
    file_content_base64: base64,
  };
  const headers = getServiceNowHeaders({
    includeBasicAuth: true,
    contentType: 'application/json',
  });
  const uploadUrl = buildServiceNowUrl('/api/x_verge_gather/smart_folders/upload_attachment');

  this.__logRequestDetails('SmartFolders upload request', {
    method: 'POST',
    url: uploadUrl,
    body: {
      ...payload,
      file_content_base64: `[base64:${base64?.length || 0} chars]`,
    },
  });

  const axiosConfig = { headers };

  const response = await axios.post(
    uploadUrl,
    payload,
    axiosConfig
  );
  return { handled: true, response };
};
FoxitWebPDFAppComponent.prototype.savePdfAction = async function () {
  try {
    if (!this.state.pdfui) return;
    const pdfDoc = await this.state.pdfui.getCurrentPDFDoc();
    const file = await pdfDoc.getFile();
    const blob = new Blob([file], { type: "application/pdf" });
    let fileName = sessionStorage.getItem("filePath") || "document.pdf";
    try {
      fileName = String(fileName).split("/").pop() || fileName;
      if (!/\.pdf$/i.test(fileName)) fileName = `${fileName}.pdf`;
    } catch (_) {}

    try {
      const uploadResp = await this.__uploadViaSmartFolders({ blob, fileName });
      if (uploadResp?.handled) {
        message.success(`Uploaded ${fileName}`);
        return;
      }
      this.downloadBlob(blob, fileName);
    } catch (e) {
      console.error("Save PDF upload failed, falling back to download", e);
      this.downloadBlob(blob, fileName);
    }
  } catch (e) {
    console.error("Save PDF failed", e);
    Modal.error({ title: '', content: `Failed to save PDF: ${this.__describeAxiosError(e)}` });
  }
};

FoxitWebPDFAppComponent.prototype.saveAsPdfAction = async function () {
  try {
    if (!this.state.pdfui) return;
    let suggested = sessionStorage.getItem("filePath") || "document.pdf";
    try {
      suggested = String(suggested).split("/").pop() || suggested;
      suggested = String(suggested).replace(/\.pdf$/i, '') + '.pdf';
    } catch (_) {}
    let value = suggested;
    const name = await new Promise((resolve) => {
      Modal.confirm({
        title: 'Save As',
        content: (
          <div>
            <div style={{ marginBottom: 8 }}>Enter a new file name</div>
            <Input defaultValue={value} onChange={(e) => { value = e.target.value; }} />
          </div>
        ),
        okText: 'Save',
        cancelText: 'Cancel',
        onOk: () => resolve(value),
        onCancel: () => resolve(null),
      });
    });
    if (name == null) return;
    let fileName = String(name || '').trim();
    if (!fileName) return;
    if (!/\.pdf$/i.test(fileName)) fileName = `${fileName}.pdf`;

    const pdfDoc = await this.state.pdfui.getCurrentPDFDoc();
    const file = await pdfDoc.getFile();
    const blob = new Blob([file], { type: "application/pdf" });

    try {
      const uploadResp = await this.__uploadViaSmartFolders({ blob, fileName });
      if (uploadResp?.handled) {
        message.success(`Uploaded ${fileName}`);
        return;
      }
      this.downloadBlob(blob, fileName);
    } catch (e) {
      console.error("Save As upload failed, falling back to download", e);
      this.downloadBlob(blob, fileName);
    }
  } catch (e) {
    console.error("Save As failed", e);
    Modal.error({ title: '', content: `Failed to Save As: ${this.__describeAxiosError(e)}` });
  }
};

FoxitWebPDFAppComponent.prototype.parsePageGroups = function (input) {
  const result = [];
  if (!input) return result;
  const cleaned = String(input).replace(/\s+/g, "");
  if (cleaned.length === 0) return result;
  const parts = cleaned.split(",");
  for (const rawPart of parts) {
    const part = rawPart.trim();
    if (!part) continue;
    if (part.includes("-")) {
      const [startStr, endStr] = part.split("-");
      const start = parseInt(startStr, 10) - 1;
      const end = parseInt(endStr, 10) - 1;
      if (Number.isFinite(start) && Number.isFinite(end)) {
        const lo = Math.min(start, end);
        const hi = Math.max(start, end);
        result.push([lo, hi]);
      }
    } else {
      const n = parseInt(part, 10) - 1;
      if (Number.isFinite(n)) result.push([n]);
    }
  }
  return result;
};

FoxitWebPDFAppComponent.prototype.downloadBlob = function (blob, fileName) {
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "document.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  } catch (_) {}
};

FoxitWebPDFAppComponent.prototype.extractPagesAction = async function () {
  try {
    if (!this.state.pdfui) return;
    let value = '1-2';
    const pageText = await new Promise((resolve) => {
      Modal.confirm({
        title: 'Extract Pages',
        content: (
          <div>
            <div style={{ marginBottom: 8 }}>Enter pages to extract (e.g., 2,4-5)</div>
            <Input defaultValue={value} onChange={(e) => { value = e.target.value; }} />
          </div>
        ),
        okText: 'OK',
        cancelText: 'Cancel',
        onOk: () => resolve(value),
        onCancel: () => resolve(null),
      });
    });
    if (pageText == null) return;
    const ranges = this.parsePageGroups(pageText);
    if (!ranges.length) { Modal.warning({ title: '', content: 'No valid pages specified.' }); return; }
    const pdfDoc = await this.state.pdfui.getCurrentPDFDoc();
    const buffers = await pdfDoc.extractPages(ranges);
    const blob = new Blob(buffers, { type: "application/pdf" });
    let fileName = sessionStorage.getItem("filePath") || "document.pdf";
    try {
      fileName = String(fileName).split("/").pop() || fileName;
      fileName = String(fileName).replace(/\.pdf$/i, "");
    } catch (_) {}
    // Compose name with actual page indices like smart_folders
    const pagesForName = [];
    for (const g of ranges) {
      if (g.length === 1) pagesForName.push(g[0] + 1);
      else {
        for (let n = g[0]; n <= g[1]; n++) pagesForName.push(n + 1);
      }
    }
    const unique = Array.from(new Set(pagesForName)).sort((a, b) => a - b);
    const outputName = `${fileName}_extract_${unique.join('_')}.pdf`;
    // Upload using Smart Folders API if context exists
    try {
      const uploadResp = await this.__uploadViaSmartFolders({
        blob,
        fileName: outputName,
      });
      if (uploadResp?.handled) {
        message.success(`Uploaded ${outputName}`);
        return;
      }
      this.downloadBlob(blob, outputName);
    } catch (e) {
      console.error("Extract upload failed, falling back to download", e);
      this.downloadBlob(blob, outputName);
    }
  } catch (e) {
    console.error("Extract pages failed", e);
    Modal.error({ title: '', content: `Failed to extract pages: ${this.__describeAxiosError(e)}` });
  }
};

FoxitWebPDFAppComponent.prototype.burstPdfAction = async function () {
  try {
    if (!this.state.pdfui) return;
    let value = '1-1';
    const input = await new Promise((resolve) => {
      Modal.confirm({
        title: 'Burst PDF',
        content: (
          <div>
            <div style={{ marginBottom: 8 }}>Enter page ranges to split into separate files (e.g., 2-4,16-23,33-36)</div>
            <Input defaultValue={value} onChange={(e) => { value = e.target.value; }} />
          </div>
        ),
        okText: 'OK',
        cancelText: 'Cancel',
        onOk: () => resolve(value),
        onCancel: () => resolve(null),
      });
    });
    if (input == null) return;
    const groups = this.parsePageGroups(input);
    if (!groups.length) { Modal.warning({ title: '', content: 'No valid page ranges specified.' }); return; }
    const proceed = await new Promise((resolve) => {
      Modal.confirm({ title: '', content: `This will upload ${groups.length} file(s). Continue?`, onOk: () => resolve(true), onCancel: () => resolve(false) });
    });
    if (!proceed) return;
    const pdfDoc = await this.state.pdfui.getCurrentPDFDoc();
    let baseName = sessionStorage.getItem("filePath") || "document.pdf";
    try {
      baseName = String(baseName).split("/").pop() || baseName;
      baseName = String(baseName).replace(/\.pdf$/i, "");
    } catch (_) {}
    let uploadedCount = 0;
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i];
      const buffers = await pdfDoc.extractPages([g]);
      const blob = new Blob(buffers, { type: "application/pdf" });
      const label = g.length === 1 ? String(g[0] + 1) : `${g[0] + 1}-${g[1] + 1}`;
      try {
        const params = getAppParams();
      const table = params.get('table');
      const sys_id = params.get('sys_id');
      let uploaded = false;
      if (table && sys_id) {
        const uploadResp = await this.__uploadViaSmartFolders({
          blob,
          fileName: `${baseName}_burst_${label}.pdf`,
        });
        uploaded = !!uploadResp?.handled;
        if (uploaded) uploadedCount += 1;
      }
      if (!uploaded) {
          this.downloadBlob(blob, `${baseName}_burst_${label}.pdf`);
      }
    } catch (err) {
      console.error("Burst upload failed, falling back to download", err);
        this.downloadBlob(blob, `${baseName}_burst_${label}.pdf`);
      }
      await new Promise((r) => setTimeout(r, 80));
    }
  if (uploadedCount > 0) {
    message.success(`Uploaded ${uploadedCount} file(s)`);
  } else {
    message.info('Files downloaded locally.');
  }
  } catch (e) {
    console.error("Burst PDF failed", e);
    Modal.error({ title: '', content: `Failed to burst PDF: ${this.__describeAxiosError(e)}` });
  }
};
